import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {RoleLink} from 'src/admin-rights-service/components/role-link/role-link';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {DOWNLOAD_FAILED_MESSAGE, DOWNLOAD_SUCCESS_MESSAGE, ROUTES} from 'src/constants';
import {MessageContext} from 'src/contexts/message-context';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {getAdminProfileReport} from 'src/openapi/admin/apis/AdminProfileReportsApi';
import {useAppDispatch} from 'src/store/hooks';

import Button from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group, Item} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {Tooltip} from '@pb/kit-component-popups';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';


import {Headline} from '../../components/headline';
import {RemoveRoleController} from '../role-profile/components/remove-role';

import {RolesPageSkeleton} from './roles-page-skeleton';
import {useRolesPermissions} from './useRolesPermissions';

import type {AccessRight} from 'src/hooks/access-rights/constants';

export const RolesPage = () => {
    const {
        isLoading,
        loadingError,
        data: roles,
        loadData: loadRoles,
    } = useRolesPermissions();
    const apiDispatch = useAppDispatch();
    const navigate = useNavigate();
    const {roleCreate, rolesOpen} = useEventTracking();
    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);

    const [selectedProfileId, setSelectedProfileId] = useState<number>(-1);
    const [highlightedProfileId, setHighlightedProfileId] = useState<number>(-1);
    const [selectedProfileIds, setSelectedProfileIds] = useState<Set<number>>(new Set());
    const [isDownloading, setIsDownloading] = useState(false);

    const profileIds = useMemo(() => roles?.map(({profileId}) => profileId) || [], [roles]);
    const isAllSelected = profileIds.length > 0 && profileIds.every(profileId => selectedProfileIds.has(profileId));
    const isPartialSelected = selectedProfileIds.size > 0 && !isAllSelected;

    const onRemove = useCallback((profileId: number) => {
        setSelectedProfileId(profileId);
    }, []);

    const setProfileSelected = useCallback((profileId: number, selected: boolean) => {
        setSelectedProfileIds(prev => {
            const next = new Set(prev);
            if (selected) {
                next.add(profileId);
            } else {
                next.delete(profileId);
            }
            return next;
        });
    }, []);

    const toggleAllCurrent = useCallback((selected: boolean) => {
        setSelectedProfileIds(prev => {
            const next = new Set(prev);
            profileIds.forEach(profileId => {
                if (selected) {
                    next.add(profileId);
                } else {
                    next.delete(profileId);
                }
            });
            return next;
        });
    }, [profileIds]);

    const clearSelection = useCallback(() => {
        setSelectedProfileIds(new Set());
    }, []);

    const downloadReport = useCallback(async () => {
        if (!selectedProfileIds.size || isDownloading) {
            return;
        }

        setIsDownloading(true);
        const response = await apiDispatch(getAdminProfileReport({
            requestBody: Array.from(selectedProfileIds),
        }));
        setIsDownloading(false);

        if (response.payload.error) {
            setErrorMessage(response.payload.data.message || DOWNLOAD_FAILED_MESSAGE);
            return;
        }

        setSuccessMessage(DOWNLOAD_SUCCESS_MESSAGE);
        clearSelection();
    }, [
        apiDispatch,
        clearSelection,
        isDownloading,
        selectedProfileIds,
        setErrorMessage,
        setSuccessMessage,
    ]);

    useEffect(() => {
        rolesOpen();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Skeleton shown={isLoading} view={<RolesPageSkeleton/>}>
            <ServerResponseHandler
                serverError={loadingError}
                sectionName="Roles"
                accessRight={ACCESS_RIGHTS.MANAGE_PERMISSIONS_FOR_A_PROFILE as AccessRight}>
                <Group direction="v">
                    <WidthItem width="100%">
                        <Headline
                            header="Admin Settings"
                            subHeader="Roles & Permissions"
                            actionButtons={(
                                <Group alignItems="center" gap="s">
                                    <Button
                                        variant="Type.SECONDARY"
                                        busy={isDownloading}
                                        disabled={!selectedProfileIds.size || isDownloading}
                                        onClick={downloadReport}>
                                        Download
                                    </Button>
                                    <Button
                                        variant="Type.PRIMARY"
                                        onClick={() => {
                                            navigate(ROUTES.CREATE_ROLE);
                                            roleCreate({
                                                place: 'Roles Page',
                                                isDuplicate: false,
                                            });
                                        }}>
                                        New Role
                                    </Button>
                                </Group>
                            )}
                        />
                    </WidthItem>
                    <Item className="group-item pd-l" grow={1}>
                        <Table>
                            <Head>
                                <Cell width={50}>
                                    <CheckBox
                                        checked={isAllSelected}
                                        indeterminate={isPartialSelected}
                                        disabled={!profileIds.length}
                                        onCheckBoxChange={({event}) => {
                                            toggleAllCurrent(event.currentTarget.checked);
                                        }}
                                    />
                                </Cell>
                                <Cell width={400}>Role Name</Cell>
                                <Cell>Description</Cell>
                                <Cell width={120} className="text-align_right">Assigned Users</Cell>
                                <Cell width={100} className="text-align_right"/>
                            </Head>
                            <Body onMouseLeave={() => {
                                setHighlightedProfileId(-1);
                            }}>
                                {roles?.map(({profileId, profileName, profileDescription, usersCounts, isPrimary}) => (
                                    <Row
                                        highlighted={profileId === highlightedProfileId}
                                        key={profileId}
                                        onMouseLeave={() => {
                                            setHighlightedProfileId(-1);
                                        }}
                                        onMouseEnter={() => {
                                            setHighlightedProfileId(profileId);
                                        }}>
                                        <Cell>
                                            <CheckBox
                                                checked={selectedProfileIds.has(profileId)}
                                                onCheckBoxChange={({event}) => {
                                                    setProfileSelected(profileId, event.currentTarget.checked);
                                                }}
                                            />
                                        </Cell>
                                        <Cell>
                                            <RoleLink caption={profileName} profileId={profileId}/>
                                        </Cell>
                                        <Cell>
                                            {profileDescription ? (
                                                <EllipsisTextHint title={profileDescription}>
                                                    {profileDescription}
                                                </EllipsisTextHint>
                                            ) : '--'}
                                        </Cell>
                                        <Cell className="text-align_right">
                                            <RoleLink
                                                caption={usersCounts.toString()}
                                                profileId={profileId}
                                                tab="assigned-users"
                                            />
                                        </Cell>
                                        <Cell>
                                            <Item className="group-item pd-xs">
                                                <Group gap="l" alignItems="center">
                                                    {profileId === highlightedProfileId && (
                                                        <Tooltip anchor={({anchorRef, getAnchorProps}) => (
                                                            <BasicIconButton
                                                                ref={anchorRef}
                                                                {...getAnchorProps()}
                                                                inline={true}
                                                                iconProps={{type: Icons.COPY}}
                                                                onClick={() => {
                                                                    navigate(ROUTES.CREATE_ROLE, {state: {
                                                                        duplicateProfileId: profileId,
                                                                    }});
                                                                    roleCreate({
                                                                        place: 'Roles Page',
                                                                        isDuplicate: true,
                                                                    });
                                                                }}
                                                            />
                                                        )}>
                                                            Duplicate Role
                                                        </Tooltip>
                                                    )}
                                                    <RemoveRoleController
                                                        onDelete={loadRoles}
                                                        profileName={profileName}
                                                        profileId={selectedProfileId}
                                                        isPrimary={isPrimary}
                                                        usersCounts={usersCounts}>
                                                        {({onShowRemoveModal, tooltip}) => {
                                                            if (profileId === highlightedProfileId) {
                                                                return (
                                                                    <Tooltip anchor={({anchorRef, getAnchorProps}) => (
                                                                        <BasicIconButton
                                                                            ref={anchorRef}
                                                                            {...getAnchorProps()}
                                                                            disabled={!!tooltip}
                                                                            iconProps={{type: Icons.TRASH_LIGHT}}
                                                                            onClick={() => {
                                                                                onShowRemoveModal();
                                                                                onRemove(profileId);
                                                                            }}
                                                                        />
                                                                    )}>
                                                                        {tooltip || 'Delete Role'}
                                                                    </Tooltip>
                                                                );
                                                            }

                                                            return null;
                                                        }}
                                                    </RemoveRoleController>
                                                </Group>
                                            </Item>
                                        </Cell>
                                    </Row>
                                ))}
                            </Body>
                        </Table>
                    </Item>
                </Group>
            </ServerResponseHandler>
        </Skeleton>
    );
};
